import { useCallback, useState } from "react";
import { supabase } from "../supabaseClient";
import { useLanguage } from "../i18n/LanguageContext";

export function useLearning() {
  const { language } = useLanguage();

  const [learningUnits, setLearningUnits] = useState([]);
  const [learningTopics, setLearningTopics] = useState([]);
  const [topicTranslations, setTopicTranslations] = useState([]);
  const [contentBlocks, setContentBlocks] = useState([]);
  const [topicExercises, setTopicExercises] = useState([]);
  const [selectedTopicTree, setSelectedTopicTree] = useState([]);

  const [loadingLearning, setLoadingLearning] = useState(false);

  const fetchLearningUnits = useCallback(async () => {
    setLoadingLearning(true);

    try {
      // 1. Always load the canonical learning units
      const { data: units, error: unitsError } = await supabase
        .from("learning_units")
        .select("*")
        .eq("status", "active")
        .order("display_order", { ascending: true });

      if (unitsError) {
        throw unitsError;
      }

      let finalUnits = units || [];

      // 2. For non-English languages, load the display translations
      if (language !== "en" && finalUnits.length > 0) {
        const unitIds = finalUnits.map((unit) => unit.id);

        const { data: translations, error: translationsError } = await supabase
          .from("learning_unit_translations")
          .select("*")
          .in("unit_id", unitIds)
          .eq("language_code", language);

        if (translationsError) {
          throw translationsError;
        }

        const translationMap = new Map(
          (translations || []).map((translation) => [
            translation.unit_id,
            translation,
          ])
        );

        finalUnits = finalUnits.map((unit) => {
          const translation = translationMap.get(unit.id);

          if (!translation) {
            return unit;
          }

          return {
            ...unit,

            // Keep canonical English values available
            canonical_unit_name: unit.unit_name,
            canonical_description: unit.description,

            // Display values
            unit_name: translation.unit_name || unit.unit_name,
            description: translation.description || unit.description,
          };
        });
      }

      setLearningUnits(finalUnits);

      return finalUnits;
    } catch (error) {
      console.error("FETCH LEARNING UNITS ERROR:", error);
      alert(error.message);
      return [];
    } finally {
      setLoadingLearning(false);
    }
  }, [language]);

  const fetchUnitTopics = async (unitId) => {
    if (!unitId) return [];

    setLoadingLearning(true);

    const { data: topics, error: topicsError } = await supabase
      .from("learning_topics")
      .select("*")
      .eq("unit_id", unitId)
      .eq("status", "active")
      .order("display_order", { ascending: true });

    if (topicsError) {
      alert(topicsError.message);
      setLoadingLearning(false);
      return [];
    }

    const topicIds = (topics || []).map((t) => t.id);

    let translations = [];

    if (language !== "en" && topicIds.length > 0) {
      const { data, error } = await supabase
        .from("learning_topic_translations")
        .select("*")
        .in("topic_id", topicIds)
        .eq("language_code", language);

      if (error) {
        alert(error.message);
      } else {
        translations = data || [];
      }
    }

    setLearningTopics(topics || []);
    setTopicTranslations(translations);
    setLoadingLearning(false);

    return topics || [];
  };

  const fetchTopicDetail = async (topicId) => {
    if (!topicId) return;

    setLoadingLearning(true);

    try {
        const descendants = getDescendantTopics(
        topicId,
        learningTopics
        );

        const topicIds = [
        topicId,
        ...descendants.map((topic) => topic.id),
        ];

        const { data: blocks, error: blocksError } = await supabase
        .from("learning_content_blocks")
        .select("*")
        .in("topic_id", topicIds)
        .eq("status", "active")
        .order("display_order", { ascending: true });

        if (blocksError) {
        throw blocksError;
        }

        let finalBlocks = blocks || [];

        if (language !== "en" && finalBlocks.length > 0) {
          const blockIds = finalBlocks.map((block) => block.id);

          const {
            data: blockTranslations,
            error: blockTranslationsError,
          } = await supabase
            .from("learning_content_block_translations")
            .select("*")
            .in("content_block_id", blockIds)
            .eq("language_code", language);

          if (blockTranslationsError) {
            throw blockTranslationsError;
          }

          const translationMap = new Map(
            (blockTranslations || []).map((translation) => [
              translation.content_block_id,
              translation.translated_content,
            ])
          );

          finalBlocks = finalBlocks.map((block) => ({
            ...block,

            // Keep the original canonical content untouched
            translated_content:
              translationMap.get(block.id) || null,
          }));
        }

        const { data: exercises, error: exercisesError } = await supabase
          .from("learning_exercises")
          .select("*")
          .in("topic_id", topicIds)
          .eq("status", "active")
          .order("display_order", { ascending: true });

        if (exercisesError) {
          throw exercisesError;
        }

        let finalExercises = exercises || [];

        if (
          (language === "es" || language === "ru") &&
          finalExercises.length > 0
        ) {
          const exerciseIds = finalExercises.map(
            (exercise) => exercise.id
          );

          const {
            data: exerciseTranslations,
            error: exerciseTranslationsError,
          } = await supabase
            .from("learning_exercise_translations")
            .select("*")
            .in("exercise_id", exerciseIds)
            .eq("language_code", language);

          if (exerciseTranslationsError) {
            throw exerciseTranslationsError;
          }

          const exerciseTranslationMap = new Map(
            (exerciseTranslations || []).map((translation) => [
              translation.exercise_id,
              translation,
            ])
          );

          finalExercises = finalExercises.map((exercise) => {
            const translation =
              exerciseTranslationMap.get(exercise.id);

            return {
              ...exercise,

              // Display-only translations
              translated_prompt:
                translation?.prompt || null,

              translated_exercise_data:
                translation?.translated_exercise_data || null,
            };
          });
        }

        setSelectedTopicTree([
        {
            ...learningTopics.find((t) => t.id === topicId),
            depth: -1,
        },
        ...descendants,
        ]);

        setContentBlocks(finalBlocks);
        setTopicExercises(finalExercises);
    } catch (error) {
        console.error("FETCH TOPIC DETAIL ERROR:", error);
        alert(error.message);
    } finally {
        setLoadingLearning(false);
    }
  };

  const getDescendantTopics = (rootTopicId, allTopics) => {
    const result = [];

    const walk = (parentId, depth = 0) => {
        const children = allTopics
        .filter((topic) => topic.parent_topic_id === parentId)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

        children.forEach((child) => {
        result.push({
            ...child,
            depth,
        });

        walk(child.id, depth + 1);
        });
    };

    walk(rootTopicId);

    return result;
    };

  return {
    learningUnits,
    learningTopics,
    topicTranslations,
    contentBlocks,
    topicExercises,
    loadingLearning,
    selectedTopicTree,

    fetchLearningUnits,
    fetchUnitTopics,
    fetchTopicDetail,
  };
}